import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, of } from 'rxjs';

import { environment } from 'src/environments/environment';
import CustomURIEncoder from 'src/app/helpers/utils/CustomURIEncoder';
import { tap } from 'rxjs/operators';
import { productId, SpaceType } from 'src/app/shared/constants/spaceTypes.constant';
import moment from 'moment/moment';

interface ProductDiscountResponse {
  productTypeId: number;
  locationNumber?: number;
  discounts: Discount[];
}

export interface Discount {
  discountAppliedFrom: string; // HH:mm:ss
  discountAppliedTo: string;   // HH:mm:ss
  discountPercentage: number;
  discountType: DiscountType;
  disabled?: boolean;
}

export enum DiscountType {
  AllDay = 'allDay',
  Morning = 'morning',
  Afternoon = 'afternoon',
  SpecificTime = 'specificTime',
}

export enum DiscountSlotResolution {
  KEEP_SLOT,
  CHANGE_TIME
}

@Injectable({
  providedIn: 'root'
})
export class TimeSlotDiscountsService {
  defaultApi = 'business-reference/slots/default';
  centreSpecificApi = 'business-reference/slots';

  private readonly availableSlots: BehaviorSubject<Discount[]> = new BehaviorSubject<Discount[]>(null);

  public availableSlots$ = this.availableSlots.asObservable();

  constructor(private readonly http: HttpClient) {}

  getDefaultSlots(spaceType: SpaceType): Observable<ProductDiscountResponse> {
    if (environment.TimeSlotsEnabled) {
      const selectedProductTypeId: number = productId[spaceType];
      const params = new HttpParams({ encoder: new CustomURIEncoder() }).set('productTypeId', selectedProductTypeId);
      return this.http
        .get<ProductDiscountResponse>(`${environment.apiUrl}${this.defaultApi}`, { params })
        .pipe(tap((res) => this.availableSlots.next(res.discounts.length ? res.discounts : null)));
    }
    return of(null);
  }

  getLocationSpecificSlots(productTypeId, locationNumber, date): Observable<any> {
    if (environment.TimeSlotsEnabled) {
      const params = new HttpParams({ encoder: new CustomURIEncoder() })
        .set('productTypeId', productTypeId)
        .set('locationNumber', locationNumber)
        .set('date', date);
      return this.http.get(`${environment.apiUrl}${this.centreSpecificApi}`, { params });
    }
    return of(null);
  }

  resetTimeslots(startTime?: string): void {
    this.updateSlots((slots) =>
      slots?.map((item) =>
        this.shouldDisable(item, startTime) ? { ...item, disabled: true } : { ...item, disabled: false }
      )
    );
  }

  updateSlots(fn: (slots: Discount[]) => Discount[]): void {
    this.availableSlots.next(fn(this.availableSlots.value));
  }

  shouldDisable(timeSlot: Discount, availableTimeFrom?: string): boolean {
    if (!availableTimeFrom) return false;
    const time1 = moment(timeSlot.discountAppliedFrom, ['HH:mm:ss', 'HH:mm', 'H:mm']);
    const time2 = moment(availableTimeFrom, ['HH:mm:ss', 'HH:mm', 'H:mm']);

    time1.set({ year: 2000, month: 0, date: 1 });
    time2.set({ year: 2000, month: 0, date: 1 });

    return time1.isBefore(time2);
  }
}
